import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBoutique } from '../../contexts/BoutiqueContext';
import { useTheme } from '../../contexts/ThemeContext';
import BoutiqueLogo from '../boutiques/BoutiqueLogo';
import LoadingSpinner from './LoadingSpinner';
import {
  Home, Store, Users, Package, ShoppingCart, 
  UserPlus, BarChart3, Settings, LogOut, Menu, X,
  TrendingUp, FileText, Bell, User, Boxes, CreditCard,
  ShoppingBag, Crown, Shield, Briefcase, Calculator,
  Receipt,
  DollarSign,
  Tag, 
} from 'lucide-react';

import { canShowCredits, canShowStock, canShowPromo } from '../../utils/creditHelper';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  const { user, logout, loading, isSuperAdmin, isManager, isEmployee } = useAuth();
  const { currentBoutique } = useBoutique();
  const { currentTheme, primaryColor, secondaryColor } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  if (loading) {
    return (
      <div className="layout-loading">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const getMenuItems = () => {
    const items = [];

    if (isSuperAdmin) {
      items.push(
        {
          path: '/',
          icon: Home,
          label: 'Dashboard',
          color: '#667eea',
          gradient: 'from-indigo-500 to-purple-600'
        },
        {
          path: '/admin/boutiques',
          icon: Store,
          label: 'Boutiques',
          color: '#10b981',
          gradient: 'from-emerald-500 to-teal-600'
        },
        {
          path: '/admin/users',
          icon: Users,
          label: 'Utilisateurs',
          color: '#3b82f6',
          gradient: 'from-blue-500 to-cyan-600'
        },
        {
          path: '/admin/analytics',
          icon: TrendingUp,
          label: 'Analytics',
          color: '#f59e0b',
          gradient: 'from-amber-500 to-orange-600'
        },
        {
          path: '/settings',
          icon: Settings,
          label: 'Paramètres',
          color: '#6b7280',
          gradient: 'from-gray-500 to-slate-600'
        }
      );
    }

    if (isManager) {
      items.push(
        {
          path: '/',
          icon: Home,
          label: 'Dashboard',
          color: '#667eea',
          gradient: 'from-indigo-500 to-purple-600'
        },
        {
          path: '/products',
          icon: Package,
          label: 'Produits',
          color: '#8b5cf6',
          gradient: 'from-violet-500 to-purple-600'
        },
        {
          path: '/expenses',
          icon: DollarSign,
          label: 'Dépenses',
          color: '#0a3124ff',
          gradient: 'from-emerald-500 to-teal-600'
        },
        {
          path: '/sales',
          icon: TrendingUp,
          label: 'Ventes',
          color: '#10b981',
          gradient: 'from-emerald-500 to-teal-600'
        }
      );
      if(canShowStock(user)) {
        items.push({
          path: '/stock',
          icon: Boxes,
          label: 'Stock',
          color: '#ef4444',
          gradient: 'from-red-500 to-rose-600'
        })
      }
      items.push(
         {
          path: '/invoices',
          icon: Receipt,
          label: 'Factures',
          color: '#f97316',
          gradient: 'from-orange-500 to-amber-600'
        },
        {
          path: '/customers',
          icon: UserPlus,
          label: 'Clients',
          color: '#06b6d4',
          gradient: 'from-cyan-500 to-blue-600'
        },
        {
          path: '/categories',
          icon: FileText,
          label: 'Catégories',
          color: '#6366f1',
          gradient: 'from-indigo-500 to-purple-600'
        },
        {
          path: '/analytics',
          icon: BarChart3,
          label: 'Rapports',
          color: '#f59e0b',
          gradient: 'from-amber-500 to-yellow-600'
        },
        {
          path: '/team',
          icon: Users,
          label: 'Équipe',
          color: '#06b6d4',
          gradient: 'from-cyan-500 to-sky-600'
        },
        
      );
      if (canShowPromo(currentBoutique)) {
        items.push(
          {
          path: '/promos',
          icon: Tag,
          label: 'Promos',
          color: '#f59e0b',
          gradient: 'from-cyan-500 to-sky-600'
        }
        );
      }
      if (canShowCredits(currentBoutique)) {
        items.push({
          path: '/credits',
          icon: CreditCard,
          label: 'Crédits',
          color: '#ec4899',
          gradient: 'from-pink-500 to-rose-600'
        });
      }

      
    }

    if (isEmployee) {
      items.push(
        {
          path: '/',
          icon: Home,
          label: 'Dashboard',
          color: '#667eea',
          gradient: 'from-indigo-500 to-purple-600'
        },
        {
          path: '/sales',
          icon: TrendingUp,
          label: 'Ventes',
          color: '#10b981',
          gradient: 'from-emerald-500 to-teal-600'
        },
        // ✅ NOUVEAU : Section Dépenses pour Employé
      {
        path: '/expenses',
        icon: DollarSign,
        label: 'Dépenses',
        color: '#0a3124ff',
        gradient: 'from-emerald-500 to-teal-600'
      },
        {
          path: '/products',
          icon: Package,
          label: 'Produits',
          color: '#8b5cf6',
          gradient: 'from-violet-500 to-purple-600'
        },
        {
          path: '/customers',
          icon: UserPlus,
          label: 'Clients',
          color: '#06b6d4',
          gradient: 'from-cyan-500 to-blue-600'
        }
      );
    }

    items.push(
      {
        path: '/profile',
        icon: User,
        label: 'Mon Profil',
        color: '#64748b',
        gradient: 'from-slate-500 to-gray-600'
      }
    );

    return items;
  };

  const menuItems = getMenuItems();

  const isActive = (path) => {
    return location.pathname === path || 
           (path !== '/' && location.pathname.startsWith(path));
  };

  const handleMenuClick = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getUserInitials = () => {
    return `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() || 'U';
  };

  // Système de rôles - IDENTIQUE À L'ORIGINAL
  const getRoleConfig = () => {
    if (isSuperAdmin) return {
      icon: Crown,
      emoji: '👑',
      color: '#3b82f6',
      gradient: 'from-blue-500 to-indigo-600',
      label: 'Super Admin'
    };

    if (isManager) return {
      icon: Briefcase,
      emoji: '🏪',
      color: currentTheme?.primaryColor || '#f59e0b',
      gradient: 'from-amber-500 to-orange-600',
      label: 'Gestionnaire'
    };
    
    if (isEmployee) return {
      icon: Shield,
      emoji: '👤',
      color: currentTheme?.primaryColor || '#10b981',
      gradient: 'from-emerald-500 to-green-600',
      label: 'Employé'
    };
    
    return {
      icon: User,
      emoji: '👤',
      color: '#6b7280',
      gradient: 'from-gray-500 to-slate-600',
      label: 'Utilisateur'
    };
  };

  const roleConfig = getRoleConfig();

  // Logo - IDENTIQUE À L'ORIGINAL
  const getBrandLogo = () => {
    if (isSuperAdmin) {
      return <ShoppingBag size={32} />;
    }
    
    if (currentBoutique?.logoUrl) {
      return (
        <BoutiqueLogo 
          boutique={currentBoutique}
          size="large"
          showPlaceholder={false}
          style={{ 
            width: '64px',
            height: '64px',
            borderRadius: '18px'
          }}
        />
      );
    }
    
    return <ShoppingBag size={32} />;
  };

  const getBrandName = () => {
    if (isSuperAdmin) {
      return {
        title: 'SmartBoutik',
        subtitle: 'Manager'
      };
    }
    
    return {
      title: currentBoutique?.name || 'SmartBoutik',
      subtitle: roleConfig.label
    };
  };

  const brandInfo = getBrandName();

  return (
    <div className="smart-layout-wrapper">
      {/* Particules flottantes - IDENTIQUE À L'ORIGINAL */}
      <div className="layout-floating-elements">
        <div className="layout-floating-element" style={{ top: '5%', left: '5%', animationDelay: '0s' }}>📊</div>
        <div className="layout-floating-element" style={{ top: '15%', right: '10%', animationDelay: '2s' }}>🛍️</div>
        <div className="layout-floating-element" style={{ top: '70%', left: '3%', animationDelay: '4s' }}>💼</div>
        <div className="layout-floating-element" style={{ bottom: '10%', right: '8%', animationDelay: '6s' }}>📦</div>
        <div className="layout-floating-element" style={{ bottom: '25%', left: '15%', animationDelay: '8s' }}>💳</div>
      </div>

      {/* ✅ OVERLAY avec click en dehors pour fermer */}
      {sidebarOpen && (
        <div 
          className="smart-sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ✅ SIDEBAR adaptée */}
      <aside className={`smart-modern-sidebar ${sidebarOpen ? 'sidebar-open' : ''} ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {/* ✅ HEADER avec bouton fermer et logo + nom horizontal */}
        <div className="smart-sidebar-header">
          {/* Bouton fermer X sur mobile seulement */}
          <button 
            className="smart-sidebar-close d-md-none"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={20} />
          </button>

          {/* ✅ LOGO + NOM HORIZONTAL comme sur capture */}
          <div className="smart-brand-section">
            <div className="smart-brand-horizontal">
              <div className="smart-brand-icon-wrapper">
                <div className="smart-brand-icon">
                  {getBrandLogo()}
                </div>
              </div>
              {!sidebarCollapsed && (
                <div className="smart-brand-text">
                  <h2 className="smart-brand-title">{brandInfo.title}</h2>
                  <p className="smart-brand-subtitle">{brandInfo.subtitle}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ✅ NAVIGATION qui prend toute la hauteur */}
        <nav className="smart-sidebar-nav">
          <div className="smart-nav-section">
            {menuItems.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div 
                  key={index}
                  className={`smart-nav-item-wrapper ${isActive(item.path) ? 'active' : ''}`}
                  onClick={() => handleMenuClick(item.path)}
                  role="button"
                  tabIndex={0}
                  style={{ '--item-color': item.color }}
                  title={sidebarCollapsed ? item.label : ''}
                >
                  <div className="smart-nav-item-content">
                    <div className="smart-nav-icon-wrapper">
                      <div className="smart-nav-icon" style={{ color: item.color }}>
                        {/* ✅ TAILLE ICÔNES comme sur capture */}
                        <IconComponent size={40} strokeWidth={2} />
                      </div>
                      <div className="smart-nav-icon-bg" style={{ backgroundColor: `${item.color}20` }}></div>
                    </div>
                    {!sidebarCollapsed && (
                      <span className="smart-nav-text">{item.label}</span>
                    )}
                    <div className="smart-nav-indicator" style={{ background: item.color }}></div>
                  </div>
                </div>
              );
            })}
          </div>
        </nav>

        {/* Section utilisateur */}
        <div className="smart-user-section">
          <div 
            className="smart-user-card"
            data-role={user.role}
          >
            <div 
              className="smart-user-info"
              onClick={() => handleMenuClick('/profile')}
              title={`Voir le profil de ${`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Utilisateur'}`}
              role="button"
              tabIndex={0}
            >
              <div className="smart-user-avatar-wrapper">
                <div 
                  className="smart-user-avatar"
                  style={{ 
                    background: `linear-gradient(135deg, ${roleConfig.color}, ${roleConfig.color}dd)` 
                  }}
                >
                  {getUserInitials()}
                </div>
                <div className="smart-user-status-dot"></div>
              </div>
              
              <div className="smart-user-details">
                <div className="smart-user-name">
                  {`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Utilisateur'}
                </div>
                <div className="smart-user-role">
                  {roleConfig.label}
                </div>
              </div>
            </div>
            
            <button 
              className="smart-logout-btn"
              onClick={handleLogout}
              title="🚪 Déconnexion"
              type="button"
              aria-label="Se déconnecter"
            >
              <span className="logout-emoji">🚪</span>
              <span className="logout-text">Déconnexion</span>
            </button>
          </div>
        </div>
      </aside>

      {/* ✅ HEADER MOBILE - MASQUÉ quand sidebar ouverte */}
      <div className={`smart-mobile-header d-md-none ${sidebarOpen ? 'hidden' : ''}`}>
        <div className="smart-mobile-header-content">
          <div className="smart-mobile-left">
            <button 
              className="smart-mobile-menu-btn"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu size={20} />
            </button>
            
            {/* Logo boutique sur mobile */}
            {!isSuperAdmin && currentBoutique && (
              <div className="smart-mobile-logo">
                <BoutiqueLogo 
                  boutique={currentBoutique}
                  size="small"
                  showPlaceholder={true}
                  style={{ 
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px'
                  }}
                />
              </div>
            )}
          </div>
          
          <div className="smart-mobile-brand">
            <span className="fw-bold">{brandInfo.title}</span>
          </div>
          
          <div 
            className="smart-mobile-user"
            onClick={() => handleMenuClick('/profile')}
            style={{ cursor: 'pointer' }}
          >
            <span className="smart-mobile-avatar">
              {getUserInitials()}
            </span>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <main className={`smart-main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {/* ✅ HEADER DESKTOP avec bouton toggle */}
        <div className="smart-desktop-header d-none d-md-flex">
          <button 
            className="smart-toggle-btn"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={sidebarCollapsed ? 'Développer le menu' : 'Réduire le menu'}
          >
            <Menu size={20} />
          </button>
        </div>

        <div className="smart-page-wrapper">
          <Outlet />
        </div>
      </main>

      <style jsx>{`
        :root {
          --surface-glass: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.25) 0%, 
            rgba(255, 255, 255, 0.18) 50%, 
            rgba(255, 255, 255, 0.12) 100%);
          --backdrop-blur: blur(20px);
          --border-glass: rgba(255, 255, 255, 0.18);
          --shadow-glass: 0 8px 32px rgba(0, 0, 0, 0.37);
          --text-white: rgba(255, 255, 255, 0.95);
        }

        .smart-layout-wrapper {
          min-height: 100vh;
          background: ${primaryColor && secondaryColor ? 
            `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` : 
            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
          position: relative;
          overflow: hidden;
        }

        .smart-layout-wrapper::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat;
          animation: bgMove 25s linear infinite;
          z-index: 0;
        }

        @keyframes bgMove {
          0% { transform: translateX(0) translateY(0); }
          100% { transform: translateX(60px) translateY(60px); }
        }

        .layout-loading {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          background: ${primaryColor && secondaryColor ? 
            `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` : 
            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
        }

        .layout-floating-elements {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          z-index: 1;
        }

        .layout-floating-element {
          position: absolute;
          font-size: 2rem;
          opacity: 0.15;
          animation: layoutFloat 8s ease-in-out infinite;
          filter: drop-shadow(0 4px 8px rgba(0,0,0,0.1));
        }

        @keyframes layoutFloat {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(15deg); }
        }

        .smart-sidebar-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(4px);
          z-index: 998;
          display: none;
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        /* ✅ SIDEBAR avec couleurs boutique */
        .smart-modern-sidebar {
          position: fixed;
          top: 0;
          left: 0;
          width: 320px;
          height: 100vh;
          background: ${primaryColor && secondaryColor ? 
            `linear-gradient(180deg, ${primaryColor} 0%, ${secondaryColor} 100%)` : 
            'var(--surface-glass)'};
          backdrop-filter: var(--backdrop-blur);
          border-right: 1px solid var(--border-glass);
          color: var(--text-white);
          transform: translateX(-100%);
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          z-index: 999;
          display: flex;
          flex-direction: column;
          box-shadow: var(--shadow-glass);
        }

        .smart-modern-sidebar.sidebar-collapsed {
  width: 100px;
}

.smart-main-content.sidebar-collapsed {
  margin-left: 100px;
}

        .smart-modern-sidebar.sidebar-open {
          transform: translateX(0);
        }

        @media (min-width: 769px) {
          .smart-modern-sidebar {
            transform: translateX(0);
          }
          
          .smart-sidebar-overlay {
            display: none !important;
          }
        }

        @media (max-width: 768px) {
          .smart-sidebar-overlay {
            display: block;
          }
          
          .smart-modern-sidebar.sidebar-collapsed {
            width: 320px;
          }
        }

        /* ✅ HEADER SIDEBAR avec bouton fermer */
        .smart-sidebar-header {
          padding: 1.5rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          background: rgba(255, 255, 255, 0.05);
          position: relative;
        }

        .smart-sidebar-close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: white;
          padding: 0.5rem;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
        }

        .smart-sidebar-close:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: scale(1.05);
        }

        /* ✅ LOGO + NOM HORIZONTAL comme sur capture */
        .smart-brand-section {
          display: flex;
          align-items: center;
          justify-content: flex-start;
        }

        .smart-brand-horizontal {
          display: flex;
          align-items: center;
          gap: 1rem;
          width: 100%;
        }

        .smart-brand-icon-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .smart-brand-icon {
          position: relative;
          z-index: 2;
          color: white;
          transition: all 0.3s ease;
        }

        .smart-brand-text {
          text-align: left;
          transition: opacity 0.3s ease;
          flex: 1;
        }

        .smart-brand-title {
          font-size: 1.3rem;
          font-weight: 700;
          margin: 0;
          color: white;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
          line-height: 1.2;
        }

        .smart-brand-subtitle {
          font-size: 0.8rem;
          opacity: 0.8;
          margin: 0;
          color: rgba(255, 255, 255, 0.9);
          line-height: 1;
        }

        /* ✅ NAVIGATION qui prend toute la hauteur */
        .smart-sidebar-nav {
          flex: 1;
          padding: 1rem 0;
          overflow-y: auto;
          overflow-x: hidden;
          display: flex;
          flex-direction: column;
        }

        .smart-nav-section {
          padding: 0 1rem;
          flex: 1;
        }

        .smart-nav-item-wrapper {
          margin-bottom: 0.5rem;
          cursor: pointer;
          border-radius: 12px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }

        .smart-nav-item-wrapper:hover {
          background: rgba(255, 255, 255, 0.1);
          transform: translateY(-1px);
        }

        .smart-nav-item-wrapper.active {
          background: rgba(255, 255, 255, 0.15);
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }

        .smart-nav-item-content {
          display: flex;
          align-items: center;
          padding: 1rem;
          position: relative;
          z-index: 2;
        }

        .sidebar-collapsed .smart-nav-item-content {
          justify-content: center;
          padding: 1rem 0.5rem;
        }

        /* ✅ ICÔNES plus grandes comme sur capture */
        .smart-nav-icon-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1rem;
          transition: all 0.3s ease;
          width: 60px;
          height: 60px;
        }

        .sidebar-collapsed .smart-nav-icon-wrapper {
          margin-right: 0;
        }

        .smart-nav-icon {
          position: relative;
          z-index: 2;
          transition: all 0.3s ease;
        }

        .smart-nav-icon-bg {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 50px;
          height: 50px;
          border-radius: 50%;
          opacity: 0.3;
          filter: blur(10px);
          z-index: 1;
          transition: all 0.3s ease;
        }

        .smart-nav-text {
          font-size: 1rem;
          font-weight: 600;
          color: white;
          flex: 1;
          transition: opacity 0.3s ease;
        }

        .smart-nav-indicator {
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          border-radius: 0 4px 4px 0;
          opacity: 0;
          transform: translateX(-100%);
          transition: all 0.3s ease;
        }

        .smart-nav-item-wrapper.active .smart-nav-indicator {
          opacity: 1;
          transform: translateX(0);
        }

        /* ✅ SECTION UTILISATEUR en bas */
        .smart-user-section {
          padding: 1rem 1.5rem;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          background: rgba(255, 255, 255, 0.05);
          margin-top: auto;
        }

        .smart-user-info {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.75rem;
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 8px;
          transition: all 0.3s ease;
        }

        .sidebar-collapsed .smart-user-info {
          justify-content: center;
          gap: 0;
        }

        .smart-user-info:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .smart-user-avatar {
          position: relative;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid rgba(255, 255, 255, 0.3);
          flex-shrink: 0;
        }

        .smart-user-initials {
          font-weight: 700;
          font-size: 0.9rem;
          color: white;
        }

        .smart-user-status {
          position: absolute;
          bottom: 2px;
          right: 2px;
          width: 10px;
          height: 10px;
          background: #10b981;
          border-radius: 50%;
          border: 2px solid white;
        }

        .smart-user-details {
          flex: 1;
          min-width: 0;
          transition: opacity 0.3s ease;
        }

        .smart-user-name {
          font-size: 0.9rem;
          font-weight: 600;
          color: white;
          margin: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .smart-user-role {
          font-size: 0.8rem;
          color: rgba(255, 255, 255, 0.7);
          margin: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .smart-logout-btn {
          width: 100%;
          background: rgba(239, 68, 68, 0.2);
          border: 1px solid rgba(239, 68, 68, 0.3);
          color: rgba(255, 255, 255, 0.9);
          padding: 0.5rem;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          cursor: pointer;
          transition: all 0.3s ease;
          font-weight: 500;
          font-size: 0.9rem;
        }

        .sidebar-collapsed .smart-logout-btn {
          gap: 0;
        }

        .smart-logout-btn:hover {
          background: rgba(239, 68, 68, 0.3);
          border-color: rgba(239, 68, 68, 0.5);
          transform: translateY(-1px);
        }

        .smart-main-content {
          margin-left: 320px;
          min-height: 100vh;
          transition: margin-left 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          position: relative;
          z-index: 1;
        }

        

        .smart-desktop-header {
          height: 60px;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          align-items: center;
          padding: 0 1.5rem;
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .smart-toggle-btn {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: white;
          padding: 0.5rem;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .smart-toggle-btn:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: translateY(-1px);
        }

        /* ✅ HEADER MOBILE - MASQUÉ quand sidebar ouverte */
        .smart-mobile-header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          background: white;
          border-bottom: 1px solid #e5e7eb;
          z-index: 1000;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          transition: opacity 0.3s ease;
        }

        .smart-mobile-header.hidden {
          opacity: 0;
          pointer-events: none;
        }

        .smart-mobile-header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
        }

        .smart-mobile-left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .smart-mobile-menu-btn {
          background: transparent;
          border: 2px solid #374151;
          color: #000000;
          padding: 0.5rem;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .smart-mobile-menu-btn:hover {
          background: #f3f4f6;
          border-color: #1f2937;
        }

        .smart-mobile-logo {
          display: flex;
          align-items: center;
        }

        .smart-mobile-brand {
          color: #1f2937;
          font-size: 1.2rem;
          font-weight: 700;
        }

        .smart-mobile-user {
          display: flex;
          align-items: center;
        }

        .smart-mobile-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: ${primaryColor || '#3b82f6'};
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.9rem;
          border: 2px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .smart-page-wrapper {
          margin: 1rem;
          border-radius: 20px;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          min-height: calc(100vh - 8rem);
          transition: all 0.3s ease;
        }

        @media (max-width: 768px) {
          .smart-main-content {
            margin-left: 0;
            padding-top: 70px;
          }

          .smart-main-content.sidebar-collapsed {
            margin-left: 0;
          }

          .smart-page-wrapper {
            margin: 0.5rem;
            border-radius: 16px;
            min-height: calc(100vh - 9rem);
          }
        }

        /* ANIMATIONS IDENTIQUES À L'ORIGINAL */
        @keyframes slideInFromLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .smart-nav-item-wrapper {
          animation: slideInFromLeft 0.6s ease-out;
          animation-fill-mode: both;
        }

        .smart-nav-item-wrapper:nth-child(1) { animation-delay: 0.1s; }
        .smart-nav-item-wrapper:nth-child(2) { animation-delay: 0.2s; }
        .smart-nav-item-wrapper:nth-child(3) { animation-delay: 0.3s; }
        .smart-nav-item-wrapper:nth-child(4) { animation-delay: 0.4s; }
        .smart-nav-item-wrapper:nth-child(5) { animation-delay: 0.5s; }
        .smart-nav-item-wrapper:nth-child(6) { animation-delay: 0.6s; }
        .smart-nav-item-wrapper:nth-child(7) { animation-delay: 0.7s; }
        .smart-nav-item-wrapper:nth-child(8) { animation-delay: 0.8s; }
        .smart-nav-item-wrapper:nth-child(9) { animation-delay: 0.9s; }
        .smart-nav-item-wrapper:nth-child(10) { animation-delay: 1.0s; }
        .smart-nav-item-wrapper:nth-child(11) { animation-delay: 1.1s; }

        .smart-nav-item-wrapper:focus-visible {
          outline: 2px solid rgba(255, 255, 255, 0.6);
          outline-offset: 2px;
        }

        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }

        .smart-user-section {
          animation: slideInUp 0.3s ease-out !important;
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .smart-user-info:focus,
        .smart-logout-btn:focus {
          outline: 2px solid rgba(255, 255, 255, 0.5) !important;
          outline-offset: 2px !important;
        }

        * {
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
      `}</style>
    </div>
  );
};

export default Layout;